import type { ComputedRef } from 'vue'
import type { LinkBreadcrumbItem, NavigationBreadcrumbsConfigItem } from '@shared/ui/menus'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { buildDisciplineTaskCommentsUrl } from '../lib/discipline-task-comments'
import { getDisciplineName } from './discipline-general-info'
import { useDisciplineStore } from './store'

interface DisciplineDetailHeaderReturn {
  config: ComputedRef<NavigationBreadcrumbsConfigItem[]>
}

const DISCIPLINE_PATH_PATTERN = /^\/disciplines\/[^/]+/

export const useDisciplineDetailHeader = (): DisciplineDetailHeaderReturn => {
  const route = useRoute()
  const disciplineId = route.params.id as string
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  /** A sibling chip opens the same tab of the sibling discipline, as the legacy page did. */
  const buildSiblingUrl = (siblingId: number): string | null => {
    if (route.path.endsWith('/task-comments')) return buildDisciplineTaskCommentsUrl(siblingId)

    return route.path.replace(DISCIPLINE_PATH_PATTERN, `/disciplines/${siblingId}`)
  }

  const config = computed<NavigationBreadcrumbsConfigItem[]>(() => {
    const customer = customerStore.getCustomer
    const discipline = disciplineStore.getDiscipline
    const kid = customer?.kids.find(k => k.id === discipline?.kid?.id) ?? null
    const kidDisciplines = kid?.disciplines ?? []
    const codeLabels = listsStore.getLists?.discipline.code

    const items: NavigationBreadcrumbsConfigItem[] = [
      {
        id: 'customers',
        text: 'Customers',
        to: AppRoute.Customers,
        disabled: false,
      },
    ]

    if (customer) {
      items.push({
        id: 'customer-detail',
        text: `Customer / ${customer.id}`,
        to: resolveUrl(AppRoute.CustomersShow, { id: customer.id }) ?? '',
        disabled: false,
      })
    }

    if (kid) {
      items.push({
        id: 'kids-detail',
        text: `Kids / ${kid.name}`,
        to: resolveUrl(AppRoute.KidsShow, { id: kid.id }) ?? '',
        disabled: false,
      })
    }

    if (kidDisciplines.length) {
      const disciplineGroup: LinkBreadcrumbItem[] = kidDisciplines.map(d => ({
        id: `discipline-${d.id}`,
        text: getDisciplineName(d.code, codeLabels),
        to: buildSiblingUrl(d.id),
        disabled: String(d.id) === disciplineId,
      }))
      items.push(disciplineGroup)
    } else if (discipline) {
      // Without the customer's kids there are no sibling chips; the open discipline still ends the trail, disabled.
      items.push({
        id: `discipline-${discipline.id}`,
        text: getDisciplineName(discipline.code, codeLabels),
        to: null,
        disabled: true,
      })
    }

    return items
  })

  return {
    config,
  }
}
